import React, { useState, useRef } from "react";
import { X, Camera, Trash2, Image, ChevronDown } from "lucide-react";
import toast from "react-hot-toast";
import PhotoCaptureButton from "./PhotoCaptureButton";
import SubmitCountdown from "./SubmitCountdown";

const FinalPhotoCapture = ({ isOpen, onClose, onPhotosCapture, isLoading }) => {
  const [photos, setPhotos] = useState([null, null]); // 2 photo slots
  const [notes, setNotes] = useState("");
  const [uploadingIndex, setUploadingIndex] = useState(null);
  const fileInputRefs = [useRef(null), useRef(null)];

  const handlePhotoCapture = (index) => {
    fileInputRefs[index].current?.click();
  };

  const handleFileSelect = async (index, e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Preview the file immediately
      const reader = new FileReader();
      reader.onload = (event) => {
        const newPhotos = [...photos];
        newPhotos[index] = {
          file,
          preview: event.target.result,
        };
        setPhotos(newPhotos);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = (index) => {
    const newPhotos = [...photos];
    newPhotos[index] = null;
    setPhotos(newPhotos);
  };

  const handleSubmit = async () => {
    // Extract files from photo objects
    const photoFiles = photos.filter((p) => p !== null).map((p) => p.file);

    try {
      await onPhotosCapture(photoFiles, notes);
      setPhotos([null, null]);
      setNotes("");
      onClose();
    } catch (err) {
      // The parent already showed the error; keep the photos so the driver can retry
      console.error("Failed to submit photos:", err);
    }
  };

  const hasPhotos = photos.some((p) => p !== null);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center">
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-lg shadow-xl p-6 max-h-96 sm:max-h-full overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-gray-900">Completion Photos</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* Info */}
        <p className="text-sm text-gray-600 mb-4">
          Capture  photos to confirm passenger drop-off completion.
        </p>

        {/* Notes Input */}
        <div className="mb-4">
          <label className="text-xs font-semibold text-gray-500 uppercase block mb-2">
            Notes
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any notes about this completion..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            rows="2"
          />
        </div>

        {/* Photo Slots */}
        <div className="space-y-4 mb-6">
          {photos.map((photo, index) => (
            <div key={index} className="flex flex-col items-center gap-3">
              <p className="text-xs font-semibold text-gray-500 uppercase w-full">
                Photo {index + 1}
              </p>

              {photo ? (
                <div className="relative w-full aspect-video rounded-lg overflow-hidden border-2 border-green-400">
                  <img
                    src={photo.preview}
                    alt={`Completion photo ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <button
                    onClick={() => handleRemovePhoto(index)}
                    className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="w-full aspect-video rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50">
                  <Camera className="w-12 h-12 text-gray-400" />
                </div>
              )}

              <PhotoCaptureButton
                onPhotoSelect={(file) =>
                  handleFileSelect(index, { target: { files: [file] } })
                }
                disabled={false}
                hasPhoto={!!photo}
                buttonText="Capture Photo"
                retakeText="Retake Photo"
              />

              <input
                ref={fileInputRefs[index]}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(e) => handleFileSelect(index, e)}
                className="hidden"
              />
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-gray-700 font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading || !hasPhotos}
            className="flex-1 px-4 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isLoading ? <SubmitCountdown label="Completing" /> : "Complete Ride"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FinalPhotoCapture;
